import { AlertTriangle, Check, Eye } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { AnomalyDetectionResult } from '@/types/anomaly';
import type { PolicyViolation } from '@/data/mockPolicyViolations';
import type { Timesheet } from '@/types/timesheet';
import { FlaggedIssuesList } from './FlaggedIssuesList';

export interface FlaggedApprovalDialogProps {
  timesheet: Timesheet | null;
  onOpenChange: (open: boolean) => void;
  /** Anomalies the current user is permitted to see. */
  anomalyResult: AnomalyDetectionResult | undefined;
  /** Violations the current user is permitted to see. */
  policyViolations: PolicyViolation[];
  approvalNotes: string;
  onApprovalNotesChange: (notes: string) => void;
  onCancel: () => void;
  onReview: () => void;
  onApproveAnyway: () => void;
}

export function FlaggedApprovalDialog({
  timesheet,
  onOpenChange,
  anomalyResult,
  policyViolations,
  approvalNotes,
  onApprovalNotesChange,
  onCancel,
  onReview,
  onApproveAnyway,
}: FlaggedApprovalDialogProps) {
  return (
    <AlertDialog
      open={!!timesheet}
      onOpenChange={onOpenChange}
    >
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Approve Flagged Timesheet?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {timesheet?.employeeName}'s timesheet has the following issues. Please review them before approving.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {timesheet && (
          <FlaggedIssuesList
            anomalyResult={anomalyResult}
            policyViolations={policyViolations}
          />
        )}

        <div className="space-y-2 my-2">
          <label className="text-sm font-medium">Add an approval note (optional)</label>
          <Textarea
            placeholder="e.g. Approved — all project codes verified."
            value={approvalNotes}
            onChange={(e) => onApprovalNotesChange(e.target.value.slice(0, 500))}
            rows={2}
          />
          <div className="flex justify-end text-xs text-muted-foreground">
            {approvalNotes.length} / 500
          </div>
        </div>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="outline"
            onClick={onReview}
          >
            <Eye className="h-4 w-4 mr-2" />
            Review Timesheet
          </Button>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onApproveAnyway}
          >
            <Check className="h-4 w-4 mr-2" />
            Approve Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
